import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError } from '../client/errors';
import { queryKeys } from '../client/query-client';
/**
 * The TSP Cohort Journey (spec §13) — the closest design-to-contract match in
 * the product. Every state-appropriate action in the design's milestone
 * timeline has a field behind it.
 */
export const MilestoneKind = z.enum(['LIVE', 'MODULE_BLOCK', 'PRESENTATION', 'SUBMISSION', 'CERTIFICATE']);
/** Derived for the reader on every request — never stored. */
export const MilestoneState = z.enum(['done', 'current', 'upcoming']);
export const ModuleState = z.enum(['done', 'current', 'upcoming', 'locked']);
export const CohortBlockModule = z.object({
    moduleId: z.string().uuid(),
    position: z.number().nonnegative(),
    title: z.string(),
    state: ModuleState,
    /**
     * The named prerequisite behind *"Unlocks when Module 8 is complete"*.
     *
     * Sequencing is gated, not merely ordered: position says what comes next, not
     * what is refused until something else is finished. The server names it, and
     * guarantees it lives in the same block — one outside could never be
     * satisfied by working through the block.
     */
    unlocksAfterModuleId: z.string().uuid().nullable(),
});
/**
 * Where a milestone can take you.
 *
 * ── `role`, not a label ───────────────────────────────────────────────────
 * The wire carries what the link IS; this client owns what the button SAYS.
 * A `label` would have been an English string on a French-first platform, and
 * it is the rule this API follows everywhere else — `contentType` rather than a
 * path, `reason.kind` rather than a rendered sentence.
 *
 * `role` also does something `(kind, state)` cannot: the `Sep 4` row carries a
 * recording AND a worksheet, and those two are indistinguishable by kind and
 * state alone. A derived verb could not tell them apart.
 *
 * ── A link you cannot reach is not listed ─────────────────────────────────
 * The server drops any whose target the caller cannot open — a draft, a premium
 * item without entitlement, a room outside their audience or an archived one.
 * So there is nothing to check here: every link that arrives resolves.
 */
export const CohortMilestoneLinkRole = z.enum([
    'RECORDING',
    'WORKSHEET',
    'GUIDE',
    'PEER_FEEDBACK',
]);
export const CohortMilestoneLink = z.discriminatedUnion('target', [
    z.object({
        id: z.string().uuid(),
        role: CohortMilestoneLinkRole,
        target: z.literal('CONTENT'),
        contentItemId: z.string().uuid(),
        contentType: z.string(),
        /** The list segment — `readings`, `qanda` — which is what routes it. */
        path: z.string(),
        title: z.string(),
    }),
    z.object({
        id: z.string().uuid(),
        role: CohortMilestoneLinkRole,
        target: z.literal('ROOM'),
        roomId: z.string().uuid(),
        name: z.string(),
    }),
]);
/**
 * What a member may read of their own submission — a different contract from
 * the reviewer's `CohortSubmission`, on purpose.
 *
 * The queue row carries `memberName`, `userId`, `cohortId` and `status`, which
 * a member either already knows or cannot use, plus a `fileUrl` minted on every
 * row. Two reads of one table are two contracts here.
 *
 * **No `fileUrl`** — see `useSubmissionFile`. That is the whole point of the
 * split we argued for and the backend built.
 */
export const MemberSubmissionFile = z.object({
    /**
     * Nullable: an upload can carry no name. Render `caseStudy.unnamedFile`
     * rather than an empty chip — a file with no name is still a file that was
     * sent, and a blank row reads as an error.
     */
    filename: z.string().nullable(),
    /** What the confirm measured, not what the client declared. */
    bytes: z.number().int().nonnegative(),
});
export const MemberSubmission = z.object({
    submittedAt: z.string().datetime(),
    reviewedAt: z.string().datetime().nullable(),
    /**
     * **Written TO the member, and defined that way in the contract** — on
     * `ReviewSubmission.note`'s own description, so a reviewer reads the rule at
     * the moment of writing rather than a member discovering it.
     *
     * That was the one question in request 17 we could not answer from here, and
     * the backend answered it now rather than later for a reason worth keeping:
     * nothing had been written yet — every note in the database was one seeded
     * string — so there was no history to leak. A note *about* a member, if ever
     * wanted, is a second differently-named column and never a redefinition of
     * this one.
     *
     * Cleared by a resubmit, along with `reviewedAt` and `reviewedByName`,
     * because all three describe the version that was replaced.
     */
    reviewNote: z.string().nullable(),
    /** Null before a review, and where the reviewer's account is gone. */
    reviewedByName: z.string().nullable(),
    /** The member's own note. Prefills the form on a resubmit. */
    body: z.string().nullable(),
    file: MemberSubmissionFile.nullable(),
});
export const CohortMilestone = z.object({
    id: z.string().uuid(),
    position: z.number().nonnegative(),
    kind: MilestoneKind,
    occursOn: z.string(),
    endsOn: z.string().nullable(),
    /** A live session's start, Montréal wall clock « HH:MM » (24 Sep 2026). */
    startTime: z.string().nullable().optional(),
    /** The session's Zoom link — shown to members at all times. */
    joinUrl: z.string().nullable().optional(),
    /** The pod this milestone is for, when it is one pod's (25 Sep 2026). Null is the whole cohort. */
    podId: z.string().uuid().nullable().optional(),
    /**
     * The Événements event this session also is (TSP request 4). The two share
     * their date, time and link; the journey offers « Voir dans Événements ».
     */
    eventId: z.string().uuid().nullable().optional(),
    title: z.string(),
    description: z.string().optional(),
    state: MilestoneState,
    /** MODULE_BLOCK only. */
    modules: z.array(CohortBlockModule).optional(),
    /**
     * Empty is the common case and a real answer — a module block takes you
     * nowhere, and neither does a milestone whose editor has not attached the
     * recording yet. Required on the wire precisely so that *no links* and *this
     * read does not answer that* stay different facts.
     */
    links: z.array(CohortMilestoneLink),
    /** SUBMISSION only — done means APPROVED, not sent. */
    submissionStatus: z
        .enum(['SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'CHANGES_REQUESTED'])
        .nullable()
        .optional(),
    /**
     * SUBMISSION only — what the member sent, and what came back.
     *
     * ── Three states, and `.nullable().optional()` is all three ──────────────
     * The backend draws the same two-level distinction `modules` already draws
     * off a MODULE_BLOCK, and both levels are used:
     *
     *   · **absent** — not a submission milestone. Nothing to ask.
     *   · **`null`** — a submission milestone with nothing sent. Most members,
     *     most of the time, and the state the panel opens its form in.
     *   · **an object** — sent. `file` is separately nullable, because written
     *     work with no PDF is a real submission the route accepts.
     *
     * ── `status` is deliberately NOT in here ─────────────────────────────────
     * It stays `submissionStatus` on the milestone. We asked for exactly that
     * and the backend held us to it when our own sketch contradicted our prose:
     * two spellings of one value on one object is two answers to one question,
     * and the day they disagree neither end can say which is right.
     */
    submission: MemberSubmission.nullable().optional(),
});
export const CohortPod = z.object({
    id: z.string().uuid(),
    name: z.string(),
    position: z.number().nonnegative(),
    /** The community room the pod talks in — the design's "Your pod's room" CTA. */
    roomId: z.string().uuid().nullable(),
    memberCount: z.number().int().nonnegative(),
});
/**
 * An étape of the programme (TSP requests 6–8, 25 Sep 2026) — « Étape 2 ·
 * Approfondir ». `state` is derived by the server from Montréal's today.
 *
 * `colour` is carried for the journey redesign, which waits on design
 * approval; nothing draws it yet. `unlocks` names the surfaces reaching this
 * étape opens — a plain string list so a surface this build has never heard
 * of cannot fail the whole journey.
 */
export const CohortStage = z.object({
    id: z.string().uuid(),
    number: z.number().int().positive(),
    title: z.string(),
    theme: z.string().nullable().default(null),
    startsOn: z.string(),
    endsOn: z.string(),
    colour: z.string().nullable().default(null),
    unlocks: z.array(z.string()).default([]),
    state: z.enum(['past', 'current', 'upcoming']).catch('upcoming'),
});
/**
 * A surface this member cannot open yet, and when they will — « Disponible à
 * l'étape 3 ». The same four facts arrive on `journey.lockedSurfaces` and in
 * the `details` of a `SURFACE_LOCKED` refusal, so one schema reads both.
 *
 * `surface` is a string rather than the one-value enum the backend declares
 * today (`caseStudies`): a second lockable surface must not break the parse.
 */
export const LockedSurface = z.object({
    surface: z.string(),
    stage: z.number().int().positive(),
    stageTitle: z.string(),
    unlocksOn: z.string(),
});
export const Cohort = z.object({
    id: z.string().uuid(),
    key: z.string(),
    name: z.string(),
    /**
     * `Cohorte 2026` — what the design writes wherever the name sits inside
     * another sentence or a 220px rail, against the full
     * `Transformer Sa Pratique — Cohorte 2026` it uses as the journey page's h1.
     *
     * Per locale, not per cohort: a short form is text, and an English cohort is
     * "Cohort 2026". `null` means nobody authored one — which the backend keeps
     * distinct from "the short form equals the full one" on purpose, since only
     * the first is a gap an editor can close. Read it through `cohortLabel`
     * rather than directly, so the fallback has one definition.
     */
    shortName: z.string().nullable(),
    description: z.string().nullable().optional(),
    startsOn: z.string(),
    endsOn: z.string(),
    months: z.number().int().positive(),
    status: z.string(),
    facilitatorId: z.string().uuid().nullable(),
    facilitatorName: z.string().nullable(),
    memberCount: z.number().int().nonnegative(),
    pods: z.array(CohortPod).optional(),
    /** The étapes, in number order. Empty when none were set — and on an older API. */
    stages: z.array(CohortStage).default([]),
    /** The étape whose dates hold today in Montréal; null before, between or after them. */
    currentStage: CohortStage.nullable().default(null),
    /** The first étape still to come — « Étape 1 débute le 30 septembre ». */
    nextStage: CohortStage.nullable().default(null),
    /** Null is live. A member never reads a deleted cohort; declared so it is not stripped. */
    deletedAt: z.string().nullable().optional(),
});
export const CohortJourney = z.object({
    cohort: Cohort,
    pod: CohortPod.nullable(),
    status: z.enum(['ENROLLED', 'COMPLETED', 'WITHDRAWN']),
    /** 0 before it starts, clamped to `months` at the end. */
    month: z.number().int().nonnegative(),
    milestonesComplete: z.number().int().nonnegative(),
    milestones: z.array(CohortMilestone),
    /**
     * Surfaces this member cannot open yet (TSP request 6). Empty once their
     * cohort has reached them — and always for staff and facilitators. The nav
     * hides what is listed here, and the surface's own route shows when it opens.
     * Nothing on the client compares dates: the day the étape starts, the server
     * stops listing it, and the next read opens the door.
     */
    lockedSurfaces: z.array(LockedSurface).default([]),
    /**
     * The one thing here that is STORED rather than derived, deliberately:
     * "issued manually by the La Boussole team … explicitly not automatic".
     * Nothing calls it on a schedule and no completion path reaches it.
     *
     * `pdfPath` is the server-declared route to the rendered document
     * (`/api/certificates/{serial}/pdf`); `useDownloadCertificate` in
     * `journey/certificates.ts` takes it as-is. The route answers 409 once
     * `revokedAt` is set, so a screen must not offer the download on a revoked
     * certificate.
     */
    certificate: z
        .object({
        serial: z.string(),
        issuedAt: z.string().datetime(),
        issuedByName: z.string().nullable(),
        fileAssetId: z.string().nullable(),
        revokedAt: z.string().datetime().nullable(),
        pdfPath: z.string(),
    })
        .nullable(),
});
/**
 * `{ journey, locale }`, and `journey` is nullable.
 *
 * The third of the four single-object envelopes the backend found undeclared —
 * the same defect as `GET /journey` and `GET /journey/challenge`. Parsing the
 * bare object here meant this query rejected every real response, so a TSP
 * member's cohort screen was dead against the API and perfect against our own
 * fixture. That is the failure mode of a hand-written mock agreeing with a
 * hand-written schema: it proves only that the two agree with each other.
 *
 * `null` is a member with no cohort, which is most of them.
 */
const CohortEnvelope = z
    .object({ journey: CohortJourney.nullable(), locale: z.string() })
    .transform((response) => response.journey);
/**
 * The name to print when the cohort is named inside something else.
 *
 * Three surfaces want the short form — the Home strip's title, the rail's
 * `YOUR PROGRAM` block, and the member pill under it — and all three want the
 * same fallback when no short form was authored. Written once so the day a
 * fourth appears it cannot pick a different answer.
 *
 * The full `name` stays the right thing for the Cohort Journey page's heading,
 * which is the one place the design spells it out.
 */
export function cohortLabel(cohort) {
    return cohort.shortName ?? cohort.name;
}
/**
 * `enabled: false` for a role that is never enrolled — the shell asks on every
 * page, and a Parent has no cohort to ask about.
 */
export function useMyCohort(http, options = {}) {
    return useQuery({
        queryKey: queryKeys.cohort.mine,
        queryFn: () => http.get('/cohorts/mine', { schema: CohortEnvelope }),
        retry: false,
        enabled: options.enabled ?? true,
    });
}
/** The lock on one surface for this member, or null when it is open to them. */
export function lockedSurface(journey, surface) {
    return journey?.lockedSurfaces.find((lock) => lock.surface === surface) ?? null;
}
/**
 * The lock a read was refused with — `403 SURFACE_LOCKED`, whose `details`
 * carry the étape and its start date. Null for any other failure, and for a
 * `details` that does not parse (the page then falls back to its ordinary
 * error rather than promising a date it does not have).
 */
export function surfaceLockFrom(error) {
    if (!(error instanceof ApiError) || error.code !== 'SURFACE_LOCKED')
        return null;
    const parsed = LockedSurface.safeParse(error.details);
    return parsed.success ? parsed.data : null;
}
/* ─── Handing in the final case study ──────────────────────────────────── */
/**
 * `POST /api/cohorts/{id}/submissions` — the member-facing half of the review.
 *
 * ── It has been reachable for one day ─────────────────────────────────────
 * The route has advertised `fileAssetId` since cohorts shipped and nothing
 * could fill it: `POST /media/uploads` judged every member against the
 * images-only allow-list, so a PDF was refused two steps earlier. The backend
 * opened the door on 2026-08-21 (`SUBMISSION` upload policy, PDF, 50 MB) and
 * this is the first client that walks through it.
 *
 * ── Either half is a submission ───────────────────────────────────────────
 * `SUBMISSION_EMPTY` fires only when `body` and `fileAssetId` are BOTH absent.
 * The screen treats the PDF as the artefact and the note as optional, which is
 * a product decision recorded in `docs/parity/18-…` — not a contract rule, and
 * the contract is the looser of the two on purpose.
 *
 * ── Sending it again replaces it, and that is not only true after a refusal ─
 * The server's guard is `APPROVED` alone. Everything else — including a row a
 * reviewer is reading right now — is overwritten, and the write nulls
 * `reviewNote`, `reviewedAt` and `reviewedBy` on its way past. So the screen
 * confirms before replacing rather than treating resubmission as a retry. See
 * request 17.
 */
export const SubmitCaseStudy = z.object({
    milestoneId: z.string().uuid(),
    /** 50 000 on the wire. Nothing here asks a member to write that much. */
    body: z.string().max(50_000).optional(),
    fileAssetId: z.string().uuid().optional(),
});
/** `CreatedRef` — the row's id, which we do not use for anything yet. */
const CreatedRef = z.object({ id: z.string().uuid() });
export function useSubmitCaseStudy(http, cohortId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => http.post(`/cohorts/${cohortId}/submissions`, SubmitCaseStudy.parse(input), {
            schema: CreatedRef,
        }),
        /*
         * Invalidate rather than write through. The response is a bare id, and the
         * only feedback a member gets today is the milestone's own
         * `submissionStatus` — which lives inside the journey payload and is
         * computed there. Re-reading it is the whole confirmation.
         */
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.cohort.mine }),
    });
}
/**
 * A refusal from the submit itself, as a key — the same split as
 * `uploadRefusalMessage`, and for the same reason: this package holds no `t`.
 *
 * `MEDIA_*` codes are deliberately absent. They arrive on the upload, one step
 * earlier, and `uploadRefusalMessage` already answers them; a second mapping
 * here would be a second place for the same code to say something different.
 */
export function submissionRefusalKey(error) {
    if (!(error instanceof ApiError))
        return null;
    switch (error.code) {
        case 'SUBMISSION_EMPTY':
            return 'cohort.caseStudy.errorEmpty';
        case 'SUBMISSION_ALREADY_APPROVED':
            // Reachable without a bug: approved in another tab, or by a reviewer
            // while this form sat open. The work is final and the answer is good
            // news, so it does not read as an error.
            return 'cohort.caseStudy.errorApproved';
        case 'MILESTONE_NOT_A_SUBMISSION':
        case 'MILESTONE_NOT_FOUND':
            return 'cohort.caseStudy.errorMilestone';
        case 'COHORT_MEMBER_NOT_FOUND':
        case 'COHORT_NOT_FOUND':
            return 'cohort.caseStudy.errorMember';
        default:
            return null;
    }
}
/**
 * `GET /cohorts/mine/submissions/{milestoneId}/file` — the bearer URL, minted
 * when the member asks and never before.
 *
 * ── Why this is not a field on the journey ───────────────────────────────
 * We argued the split and the backend built it: `fileUrl` carries the short
 * `MEDIA_URL_TTL_SECONDS` window, and `/cohorts/mine` is a payload TanStack
 * Query caches and a member holds on screen for ten minutes. A short-lived
 * capability inside a long-lived cached payload is a broken link by
 * construction — the same failure the API's own `MediaQuery` note describes for
 * a media grid whose signed rows expire where they sit.
 *
 * ── A mutation, and that is not a misuse ─────────────────────────────────
 * It is a GET, but `useQuery` would cache it — which is the one thing this
 * value must not do. `useMutation` gives us "run when asked, keep nothing",
 * which is the actual semantic. Nothing here stores the URL: it is handed
 * straight to a tab and dropped.
 *
 * ── Keyed on the MILESTONE, not the submission ───────────────────────────
 * Our sketch put a submission id in the path; the backend moved it and the
 * reasoning is better than ours. `(milestone, caller)` is the unique key the
 * table already carries, so the only identity in the lookup is the caller's own
 * and **no id in the path resolves to another member's submission at all**.
 * It also means the journey need not carry a submission id — which it does not.
 */
export const SubmissionFile = z.object({ fileUrl: z.string() });
export function useSubmissionFile(http) {
    return useMutation({
        mutationFn: (milestoneId) => http
            .get(`/cohorts/mine/submissions/${milestoneId}/file`, { schema: SubmissionFile })
            .then((answer) => answer.fileUrl),
    });
}
/**
 * The two refusals the mint can answer, both 404.
 *
 * `SUBMISSION_HAS_NO_FILE` should be unreachable from our screen — the button
 * only renders when `submission.file` is non-null — so meeting it means the row
 * moved under us, which is a real thing on a two-tab session.
 */
export function submissionFileRefusalKey(error) {
    if (!(error instanceof ApiError))
        return null;
    switch (error.code) {
        case 'SUBMISSION_NOT_FOUND':
            return 'cohort.caseStudy.errorNoSubmission';
        case 'SUBMISSION_HAS_NO_FILE':
            return 'cohort.caseStudy.errorNoFile';
        default:
            return null;
    }
}
//# sourceMappingURL=cohort.js.map